/**
 * Tenant Selector Component
 *
 * Dropdown for switching between tenants when user has access to multiple.
 * Only shows if user has 2+ available tenants.
 */

import { useState } from 'react';
import { Dropdown, Typography, Space, message, Spin } from 'antd';
import { SwapOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@api/index';
import { useAuthStore } from '@store/authStore';

const { Text } = Typography;

export default function TenantSelector() {
  const queryClient = useQueryClient();
  const { tenant, setTenant } = useAuthStore();
  const [open, setOpen] = useState(false);

  // Fetch available tenants
  const { data: meData, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.getMe(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const availableTenants = meData?.availableTenants || [];

  // Switch tenant mutation
  const switchMutation = useMutation({
    mutationFn: (tenantId: string) => authApi.switchTenant(tenantId),
    onSuccess: (tenantContext) => {
      setTenant(tenantContext);
      localStorage.setItem('bizops-tenant', tenantContext.tenantId);
      // Invalidate all queries to refetch with new tenant context
      queryClient.invalidateQueries();
      message.success(`Switched to ${tenantContext.tenantName}`);
      setOpen(false);
    },
    onError: () => {
      message.error('Failed to switch tenant');
    },
  });

  // Don't render (just text) if loading
  if (isLoading) {
    return (
      <Text type="secondary" style={{ fontSize: 13 }}>
        {tenant?.tenantName}
      </Text>
    );
  }

  const menuItems = availableTenants.map((t) => ({
    key: t.slug,
    label: (
      <Space>
        {t.id === tenant?.tenantId && <CheckOutlined style={{ color: '#52c41a' }} />}
        <span>{t.name}</span>
        <Text type="secondary" style={{ fontSize: 11 }}>({t.role})</Text>
      </Space>
    ),
    onClick: () => {
      if (t.id !== tenant?.tenantId) {
        switchMutation.mutate(t.id);
      }
    },
  }));

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Space
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 4,
          background: open ? 'var(--layout-content-bg, #f5f5f5)' : 'transparent',
        }}
      >
        {switchMutation.isPending ? (
          <Spin size="small" />
        ) : (
          <>
            <SwapOutlined style={{ fontSize: 12, color: '#888' }} />
            <Text style={{ fontSize: 13 }}>{tenant?.tenantName}</Text>
            <DownOutlined style={{ fontSize: 10, color: '#888' }} />
          </>
        )}
      </Space>
    </Dropdown>
  );
}
